// BookingSection.tsx
import React, { useMemo } from "react";
import { AvailableSlot } from "./types";
import "../../style/css/base.css";
import "../../style/css/AppointmentBooking.css";

interface BookingSectionProps {
  availableSlots: AvailableSlot[];
  selectedDate: string | null;
  selectedTime: string | null;
  onSelectDate: (date: string) => void;
  onSelectTime: (time: string) => void;
  currentMonth: Date;
  onMonthChange: (date: Date) => void;
  isLoadingSlots: boolean;
}

const BookingSection: React.FC<BookingSectionProps> = ({
  availableSlots,
  selectedDate,
  selectedTime,
  onSelectDate,
  onSelectTime,
  currentMonth,
  onMonthChange,
  isLoadingSlots,
}) => {
  // Get today's date in browser's timezone
  const getTodayInBrowserTimezone = (): string => {
    const now = new Date();
    const year = now.getFullYear();
    const month = (now.getMonth() + 1).toString().padStart(2, "0");
    const day = now.getDate().toString().padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  // Check if a date is in the past (relative to browser's timezone)
  const isPastDate = (dateString: string): boolean => {
    const today = getTodayInBrowserTimezone();
    return dateString < today;
  };

  const formatDate = (dateString: string) => {
    const [year, month, day] = dateString.split("-").map(Number);
    const date = new Date(year, month - 1, day);
    return date.toLocaleDateString("es-ES", {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
    });
  };

  const getTimeSlotsForSelectedDate = () => {
    if (!selectedDate) return [];
    const slot = availableSlots.find((s) => s.date === selectedDate);
    return slot ? slot.timeSlots : [];
  };

  const handlePrevMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() - 1);

    // Don't allow navigating to months in the past (except current month)
    const today = new Date();
    const newMonthStart = new Date(
      newDate.getFullYear(),
      newDate.getMonth(),
      1
    );
    const currentMonthStart = new Date(
      today.getFullYear(),
      today.getMonth(),
      1
    );

    if (newMonthStart >= currentMonthStart) {
      onMonthChange(newDate);
    }
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentMonth);
    newDate.setMonth(newDate.getMonth() + 1);
    onMonthChange(newDate);
  };

  // Generate proper calendar grid
  const calendarDays = useMemo(() => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const today = getTodayInBrowserTimezone();
    const todayDate = new Date(today);

    // First day of the month
    const firstDay = new Date(year, month, 1);
    // Last day of the month
    const lastDay = new Date(year, month + 1, 0);

    // Day of week for first day (0 = Sunday, 1 = Monday, etc.)
    const firstDayOfWeek = firstDay.getDay();
    // Adjust for Monday as first day of week (0 = Monday, 6 = Sunday)
    const startOffset = firstDayOfWeek === 0 ? 6 : firstDayOfWeek - 1;

    const days = [];
    const totalDays = lastDay.getDate();

    // Add empty cells for days before the first day of the month
    for (let i = 0; i < startOffset; i++) {
      days.push(null);
    }

    // Add all days of the month
    for (let day = 1; day <= totalDays; day++) {
      const date = new Date(year, month, day);
      // Format date as YYYY-MM-DD in browser's timezone
      const yearStr = date.getFullYear();
      const monthStr = (date.getMonth() + 1).toString().padStart(2, "0");
      const dayStr = date.getDate().toString().padStart(2, "0");
      const dateString = `${yearStr}-${monthStr}-${dayStr}`;

      // Check if date is in the past
      const isPast = isPastDate(dateString);

      // Date is available only if:
      // 1. It's not in the past
      // 2. It exists in availableSlots
      const isAvailable =
        !isPast && availableSlots.some((slot) => slot.date === dateString);

      days.push({
        date: dateString,
        dayNumber: day,
        isPast,
        isAvailable,
        timeSlots:
          availableSlots.find((slot) => slot.date === dateString)?.timeSlots ||
          [],
      });
    }

    return days;
  }, [currentMonth, availableSlots]);

  const hasAvailableDays = calendarDays.some((day) => day && day.isAvailable);

  // Disable previous month button if we're at current month
  const today = new Date();
  const currentMonthStart = new Date(today.getFullYear(), today.getMonth(), 1);
  const displayedMonthStart = new Date(
    currentMonth.getFullYear(),
    currentMonth.getMonth(),
    1
  );
  const isPrevMonthDisabled = displayedMonthStart <= currentMonthStart;

  return (
    <section id="booking">
      <h2>Agenda tu Cita</h2>

      <div className="card">
        <h3>Selecciona el día</h3>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "var(--space-md)",
          }}
        >
          <button
            className="btn btn--secondary"
            onClick={handlePrevMonth}
            style={{
              padding: "0.5rem 1rem",
              opacity: isPrevMonthDisabled ? 0.5 : 1,
              cursor: isPrevMonthDisabled ? "not-allowed" : "pointer",
            }}
            disabled={isPrevMonthDisabled}
          >
            &#10094; Anterior
          </button>

          <h4 style={{ margin: 0, color: "var(--color-secondary)" }}>
            {currentMonth.toLocaleDateString("es-ES", {
              month: "long",
              year: "numeric",
            })}
          </h4>

          <button
            className="btn btn--secondary"
            onClick={handleNextMonth}
            style={{ padding: "0.5rem 1rem" }}
          >
            Siguiente &#10095;
          </button>
        </div>

        {/* Loading State Overlay */}
        {isLoadingSlots ? (
          <div style={{ textAlign: "center", padding: "2rem", color: "#666" }}>
            Buscando horarios disponibles...
          </div>
        ) : (
          <div className="calendar-grid">
            <div className="day-header-booking">Lun</div>
            <div className="day-header-booking">Mar</div>
            <div className="day-header-booking">Mié</div>
            <div className="day-header-booking">Jue</div>
            <div className="day-header-booking">Vie</div>
            <div
              className="day-header-booking"
              style={{ color: "var(--error)" }}
            >
              Sáb
            </div>
            <div
              className="day-header-booking"
              style={{ color: "var(--error)" }}
            >
              Dom
            </div>

            {!hasAvailableDays && (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: "1rem",
                }}
              >
                No hay horarios disponibles para este mes con los filtros
                seleccionados.
              </div>
            )}

            {calendarDays.map((day, index) => {
              if (day === null) {
                return (
                  <div key={`empty-${index}`} className="date-cell empty"></div>
                );
              }

              const isSelected = selectedDate === day.date;
              const isAvailable = day.isAvailable;
              const isPast = day.isPast;

              return (
                <div
                  key={day.date}
                  className={`date-cell ${isSelected ? "selected" : ""} ${
                    !isAvailable ? "unavailable" : ""
                  } ${isPast ? "past-day" : ""}`}
                  onClick={() => isAvailable && onSelectDate(day.date)}
                  style={{
                    cursor: isAvailable ? "pointer" : "default",
                    opacity: isPast ? 0.3 : isAvailable ? 1 : 0.4,
                    backgroundColor: isPast ? "#f0f0f0" : "transparent",
                    color: isPast ? "#999" : "inherit",
                  }}
                  title={isPast ? "Día pasado" : ""}
                >
                  {day.dayNumber}
                  {isPast && (
                    <div
                      style={{
                        position: "absolute",
                        top: "50%",
                        left: "50%",
                        width: "80%",
                        height: "1px",
                        backgroundColor: "#999",
                        transform: "translate(-50%, -50%) rotate(-45deg)",
                      }}
                    ></div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      {/* Time Slots Section */}
      <div className="card">
        <h3>Selecciona la hora</h3>
        {selectedDate ? (
          <>
            <p
              style={{
                fontSize: "0.9rem",
                color: "var(--success)",
                marginBottom: "var(--space-sm)",
              }}
            >
              Disponibles para el {formatDate(selectedDate)}.
            </p>
            <div className="slots-container">
              {getTimeSlotsForSelectedDate().map((time) => (
                <button
                  key={time}
                  className={`slot-btn ${
                    selectedTime === time ? "selected" : ""
                  }`}
                  onClick={() => onSelectTime(time)}
                >
                  {time}
                </button>
              ))}
            </div>
          </>
        ) : (
          <p>Selecciona una fecha para ver los horarios.</p>
        )}
      </div>
    </section>
  );
};

export default BookingSection;
